import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../Component/server';

import { submitPurchase } from './modal/purchaseRequest';

const endpoint = `${SERVER_ADDRESS}product/waitlist`;
export async function joinRestockWait(liveSeq: number, productSeq: number, optionSeq: number | null, quantity: number) {
  const { data } = await api.post(endpoint, { live_seq: liveSeq, product_seq: productSeq, option_seq: optionSeq || 0, quantity });
  window.dispatchEvent(new Event('fanbird-waitlist-changed'));
  return data;
}
type Offer = { productSeq: number; optionSeq: number | null; quantity: number; availableQuantity?: number; productName: string; optionName: string; expiresAt: number };
type Entry = { group: string; waiting: boolean; offer: Offer | null };

export function hasProductWaiting(groups: string[], product: { seq: number; live_seq: number }) {
  return groups.some(group => group.startsWith(`${product.live_seq}:${product.seq}:`));
}

export function isProductWaiting(groups: string[], product: { seq: number; live_seq: number; option_check: string; unique_option1?: string[]; unique_option2?: string[] }) {
  return hasProductWaiting(groups, product);
}

export default function RestockWaitPanel({ liveSeq, enabled, onConfirmed, onWaitingChange }: { liveSeq: number; enabled: boolean; onConfirmed?: () => void; onWaitingChange?: (groups: string[]) => void }) {
  const [entries, setEntries] = useState<Entry[]>([]), [now, setNow] = useState(Date.now()), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const sending = useRef(false), active = useRef(true), scope = useRef(liveSeq);
  const authorization = useRef<string | undefined>(undefined);
  const waitingCallback = useRef(onWaitingChange);
  waitingCallback.current = onWaitingChange;
  scope.current = liveSeq;

  const load = useCallback(async () => {
    try {
      const response = await api.get(endpoint, { params: { live_seq: liveSeq }, timeout: 5000 });
      const data = response.data;
      const token = response.config?.headers?.Authorization;
      if (typeof token === 'string') authorization.current = token;
      if (active.current && scope.current === liveSeq && Array.isArray(data?.items)) {
        setNow(Date.now()); setEntries(data.items);
        waitingCallback.current?.(data.items.filter((entry: Entry) => entry.waiting || entry.offer).map((entry: Entry) => entry.group));
      }
    } catch { /* Poll failures do not blink or dismiss an existing invitation. */ }
  }, [liveSeq]);
  useEffect(() => {
    active.current = enabled; setEntries([]); setError('');
    if (!enabled) return;
    void load();
    window.addEventListener('fanbird-waitlist-changed', load);
    const leave = () => {
      void fetch(`${endpoint}/leave`, { method: 'POST', credentials: 'include', keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', ...(authorization.current ? { Authorization: authorization.current } : {}) },
        body: new URLSearchParams({ live_seq: String(liveSeq) }).toString() }).catch(() => undefined);
    };
    window.addEventListener('pagehide', leave);
    window.addEventListener('pageshow', load);
    return () => {
      active.current = false; window.removeEventListener('fanbird-waitlist-changed', load);
      window.removeEventListener('pagehide', leave);
      window.removeEventListener('pageshow', load);
      // StrictMode replays the same mounted effect without leaving the live screen.
      queueMicrotask(() => { if (!active.current || scope.current !== liveSeq) leave(); });
    };
  }, [enabled, load]);
  useEffect(() => {
    if (!enabled || !entries.length) return;
    const clock = window.setInterval(() => setNow(Date.now()), 200);
    const poll = window.setInterval(() => void load(), 1000);
    return () => { clearInterval(clock); clearInterval(poll); };
  }, [enabled, entries.length, load]);
  const entry = entries.find(row => row.offer && row.offer.expiresAt > now), offer = entry?.offer;
  useEffect(() => { if (offer) { setError(''); setQuantity(1); } }, [entry?.group, offer?.expiresAt]);
  const action = async (type: 'leave' | 'begin' | 'retain' | 'complete', group: string) => {
    await api.post(`${endpoint}/action`, { group, action: type });
  };
  const add = async () => {
    if (!entry || !offer || sending.current || offer.expiresAt <= Date.now()) return;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > (offer.availableQuantity ?? offer.quantity)) {
      setError('구매할 수량을 다시 선택해주세요.'); return;
    }
    sending.current = true; setBusy(true); setError('');
    try {

      await action('begin', entry.group);
      await submitPurchase({ live_seq: liveSeq, product_seq: offer.productSeq, option_seq: offer.optionSeq, order_cnt: quantity });
      await action('complete', entry.group); onConfirmed?.();
    } catch (cause: any) {
      const message = cause?.response?.data?.message || cause?.message || '담기 결과를 확인하지 못했습니다.';
      if (/재고|품절/.test(message)) {
        await action('retain', entry.group).catch(() => undefined);
        if (active.current) setError('이미 품절되어 대기를 유지합니다.');
      } else if (active.current) setError(message);
    } finally { sending.current = false; if (active.current) { setBusy(false); await load(); } }
  };
  if (!enabled) return null;
  return <>
    {offer && entry && <Panel role="dialog" aria-label="재입고 상품 담기">
      <Header><strong>상품을 다시 담을 수 있어요</strong><time role="timer" aria-live="off">{Math.max(0, Math.min(10, Math.ceil((offer.expiresAt - now) / 1000)))}초</time></Header>
      <Product>{offer.productName}{offer.optionName && <small>{offer.optionName}</small>}</Product>
      <Quantity>구매 수량<input aria-label="재입고 구매 수량" type="number" inputMode="numeric" min={1}
        max={offer.availableQuantity ?? offer.quantity} value={quantity} disabled={busy} onChange={event => setQuantity(Number(event.target.value))} /></Quantity>
      {error && <p role="status">{error}</p>}
      <Progress aria-hidden="true"><i style={{ transform: `scaleX(${Math.max(0, Math.min(1, (offer.expiresAt - now) / 10000))})` }} /></Progress>
      <Actions><button type="button" disabled={busy} onClick={() => { void action('retain', entry.group).then(load).catch(() => setError('대기를 유지하지 못했습니다.')); }}>취소</button>
        <button type="button" className="primary" disabled={busy} onClick={() => void add()}>{busy ? '담는 중…' : '담기'}</button></Actions>
    </Panel>}
    {error && !offer && <Feedback role="status">{error}</Feedback>}
  </>;
}
const Panel = styled.section`
  position: fixed; z-index: 1500; left: 16px; right: 16px; bottom: calc(16px + env(safe-area-inset-bottom));
  box-sizing: border-box; max-width: 420px; margin-inline: auto; padding: 16px; border: 1px solid var(--user-border);
  max-height: calc(100vh - 32px - env(safe-area-inset-bottom));
  max-height: calc(100dvh - 32px - env(safe-area-inset-bottom)); overflow-y: auto;
  border-radius: 16px; background: var(--user-surface); color: var(--user-text); box-shadow: var(--user-shadow); font-size: 15px;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  strong { min-width: 0; overflow-wrap: anywhere; }
  time { flex: none; white-space: nowrap; color: var(--user-accent); font-variant-numeric: tabular-nums; }
`;
const Product = styled.p`margin: 12px 0; overflow-wrap: anywhere; small { display: block; margin-top: 4px; color: var(--user-text-muted); }`;
const Quantity = styled.label`display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px;
  input { box-sizing: border-box; width: 90px; min-height: 44px; padding: 8px; font: inherit; color: var(--user-text); background: var(--user-surface); border: 1px solid var(--user-border); border-radius: 8px; }`;
const Progress = styled.div`
  height: 6px; background: var(--user-border); overflow: hidden; border-radius: 6px;
  i {
    display: block; height: 100%; background: var(--user-accent); transform-origin: left;
    transition: transform 200ms linear;
  }
  @media (prefers-reduced-motion: reduce) { i { transition: none; } }
`;
const Actions = styled.div`
  display: flex; gap: 8px; margin-top: 12px;
  button { flex: 1; min-height: 44px; border: 1px solid var(--user-border); border-radius: 8px; background: var(--user-surface); color: var(--user-text); font: inherit; cursor: pointer; }
  .primary { background: var(--user-accent); color: var(--user-accent-contrast, #fff); border-color: transparent; }
  button:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
  button:disabled { cursor: default; }
`;
const Feedback = styled.div`position: fixed; z-index: 1501; left: 16px; right: 16px; bottom: calc(16px + env(safe-area-inset-bottom)); padding: 12px; border-radius: 8px; background: var(--user-surface); color: var(--user-text); font-size: 14px;`;
